import { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogIn, Mail, Chrome, ArrowLeft, Eye, EyeOff, Send } from "lucide-react";
import { useLang } from "@/hooks/use-lang";
import { useAuth } from "@/hooks/use-auth";
import { track } from "@/lib/analytics";
import { AUTH_T } from "@/lib/auth-i18n";
import { consumePostLoginRedirect, replaceWithPostLoginRedirect, splitNavigationTarget } from "@/lib/auth-redirect";


export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const t = AUTH_T[lang];
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [unconfirmedEmail, setUnconfirmedEmail] = useState("");
  const [accepted, setAccepted] = useState(false);

  function getGoogleRedirectUri() {
    return window.location.origin;
  }


  // Check for confirmation success from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("type") === "signup" || params.get("confirmed") === "true") {
      setMessage(t.emailConfirmed);
      setMode("login");
    }
    if (params.get("error")) {
      setError(params.get("error_description") || t.somethingWrong);
    }
  }, [t]);

  async function handleEmailAuth(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setMessage("");
    if (mode === "signup" && !accepted) {
      setError(t.mustAccept);
      return;
    }
    setLoading(true);
    setUnconfirmedEmail("");
    track("login_started", { provider: "email", mode });

    try {
      if (mode === "login") {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) {
          // Detect unconfirmed email
          if (error.message?.toLowerCase().includes("email not confirmed") ||
              error.message?.toLowerCase().includes("not confirmed") ||
              error.code === "email_not_confirmed") {
            setUnconfirmedEmail(email);
            throw new Error(t.emailNotConfirmed);
          }
          throw error;
        }
        const confirmedSession = data.session ?? (await supabase.auth.getSession()).data.session;
        if (!confirmedSession) throw new Error(t.somethingWrong);
        safeNavigateAfterLogin();
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/login" },
        });
        if (error) throw error;
        track("account_created", { provider: "email" });
        setMessage(t.checkEmail);
      }
    } catch (err: any) {
      setError(err.message || t.somethingWrong);
    } finally {
      setLoading(false);
    }
  }

  async function handleResendConfirmation() {
    if (!unconfirmedEmail) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: unconfirmedEmail,
        options: { emailRedirectTo: window.location.origin + "/login" },
      });
      if (error) throw error;
      setMessage(t.checkEmail);
      setUnconfirmedEmail("");
    } catch (err: any) {
      setError(err.message || t.somethingWrong);
    } finally {
      setLoading(false);
    }
  }

  // TanStack Router rejeita `to` com query/hash ("Invariant failed"): separar sempre.
  function safeNavigateAfterLogin() {
    const target = consumePostLoginRedirect("/");
    const { to, search, hash } = splitNavigationTarget(target);
    try {
      navigate({ to, search, hash } as never);
    } catch {
      window.location.assign(target);
    }
  }

  // If already authenticated, leave the login page
  useEffect(() => {
    if (!authLoading && isAuthenticated) safeNavigateAfterLogin();
  }, [authLoading, isAuthenticated, navigate]);

  async function handleGoogleAuth() {
    setError("");
    if (mode === "signup" && !accepted) {
      setError(t.mustAccept);
      return;
    }
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: getGoogleRedirectUri(),
        extraParams: { prompt: "select_account" },
      });
      if (result.error) {
        setError(result.error.message || t.googleFailed);
        setLoading(false);
        return;
      }

      if (!result.redirected) {
        // Session was set in-page (popup flow) — force a clean reload so every
        // header/button reads the persisted session immediately.
        replaceWithPostLoginRedirect("/");
        return;
      }
    } catch (err: any) {
      setError(err?.message || t.googleFailed);
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 pt-6 pb-10 sm:justify-center sm:pt-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex items-center gap-2">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            {t.back}
          </Link>
        </div>

        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {mode === "login" ? t.welcomeBack : t.createAccount}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "login" ? t.signInSubtitle : t.signUpSubtitle}
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          onClick={handleGoogleAuth}
          disabled={loading}
        >
          <Chrome className="h-4 w-4" />
          {loading ? t.pleaseWait : t.continueGoogle}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">{t.or}</span>
          </div>
        </div>

        <Tabs value={mode} onValueChange={(v) => { setMode(v as "login" | "signup"); setError(""); setMessage(""); setUnconfirmedEmail(""); }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">{t.signIn}</TabsTrigger>
            <TabsTrigger value="signup">{t.signUp}</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <EmailForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              onSubmit={handleEmailAuth}
              loading={loading}
              error={error}
              message={message}
              submitLabel={t.signIn}
              emailLabel={t.email}
              passwordLabel={t.password}
              loadingLabel={t.pleaseWait}
            />
            {unconfirmedEmail && (
              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={handleResendConfirmation}
                  disabled={loading}
                >
                  <Send className="h-3 w-3" />
                  {t.resendConfirmation}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="signup">
            <EmailForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              onSubmit={handleEmailAuth}
              loading={loading}
              error={error}
              message={message}
              submitLabel={t.createAccountBtn}
              emailLabel={t.email}
              passwordLabel={t.password}
              loadingLabel={t.pleaseWait}
            />
            <div className="mt-4 flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3">
              <Checkbox
                id="accept-terms"
                checked={accepted}
                onCheckedChange={(v) => setAccepted(v === true)}
                className="mt-0.5"
              />
              <label htmlFor="accept-terms" className="text-xs leading-relaxed text-muted-foreground cursor-pointer select-none">
                {t.acceptPrefix}{" "}
                <Link to="/terms" target="_blank" className="underline text-foreground hover:text-brand">
                  {t.termsLabel}
                </Link>{" "}
                {t.andText}{" "}
                <Link to="/privacy" target="_blank" className="underline text-foreground hover:text-brand">
                  {t.privacyLabel}
                </Link>
                .
              </label>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function EmailForm({
  email, setEmail, password, setPassword,
  showPassword, setShowPassword, onSubmit, loading, error, message, submitLabel,
  emailLabel, passwordLabel, loadingLabel,
}: {
  email: string; setEmail: (v: string) => void;
  password: string; setPassword: (v: string) => void;
  showPassword: boolean; setShowPassword: (v: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean; error: string; message: string;
  submitLabel: string;
  emailLabel: string; passwordLabel: string; loadingLabel: string;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}
      {message && <p className="rounded-md bg-green-100 px-3 py-2 text-xs text-green-700">{message}</p>}

      <div className="space-y-2">
        <Label htmlFor="email">{emailLabel}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            className="pl-9"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">{passwordLabel}</Label>
        <div className="relative">
          <LogIn className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            className="pl-9 pr-10"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? loadingLabel : submitLabel}
      </Button>
    </form>
  );
}
